import React, { useCallback, useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'

// Score → circle colour (matches CSS variables)
function scoreToColor(score) {
  if (score == null) return '#8892a4'
  if (score >= 80) return '#22c55e'
  if (score >= 65) return '#84cc16'
  if (score >= 50) return '#f59e0b'
  if (score >= 35) return '#f97316'
  return '#ef4444'
}

const DEFAULT_CENTER = [118.0, -2.5]  // Indonesia
const DEFAULT_ZOOM   = 5

export default function FluxMap({
  activeTab,
  onMapClick,
  markerPos,
  assessmentMarker,    // {lat, lon, score} — shows score badge
  scanResult,          // GeoJSON FeatureCollection from area scan
  compareQueue,        // [{lat, lon, label}]
  onScanPointClick,
}) {
  const mapRef   = useRef(null)
  const mapObj   = useRef(null)
  const markerEl = useRef(null)
  const popupRef = useRef(null)

  // Initialise map
  useEffect(() => {
    if (mapObj.current) return
    mapObj.current = new maplibregl.Map({
      container: mapRef.current,
      style: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: false,
    })

    mapObj.current.addControl(new maplibregl.NavigationControl(), 'top-right')
    mapObj.current.addControl(
      new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }),
      'bottom-right'
    )

    mapObj.current.on('load', () => {
      // Scan results source + layer (added later)
      mapObj.current.addSource('scan-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })

      mapObj.current.addLayer({
        id: 'scan-circles',
        type: 'circle',
        source: 'scan-source',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['zoom'],
            4, 6,
            8, 14,
          ],
          'circle-color': [
            'case',
            ['==', ['get', 'is_excluded'], true], '#ef4444',
            ['>=', ['get', 'overall_score'], 80], '#22c55e',
            ['>=', ['get', 'overall_score'], 65], '#84cc16',
            ['>=', ['get', 'overall_score'], 50], '#f59e0b',
            ['>=', ['get', 'overall_score'], 35], '#f97316',
            '#ef4444',
          ],
          'circle-opacity': 0.85,
          'circle-stroke-color': 'rgba(0,0,0,.4)',
          'circle-stroke-width': 1,
        },
      })

      // Hover popup on scan circles
      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
      })
      popupRef.current = popup

      mapObj.current.on('mouseenter', 'scan-circles', (e) => {
        mapObj.current.getCanvas().style.cursor = 'pointer'
        const props = e.features[0].properties
        const coords = e.features[0].geometry.coordinates
        popup.setLngLat(coords)
          .setHTML(
            `<strong>Score: ${props.overall_score ?? '—'}</strong><br/>` +
            `LCOE: $${props.lcoe_usd_per_mwh?.toFixed?.(1) ?? '—'}/MWh<br/>` +
            `Yield: ${props.annual_yield_mwh?.toLocaleString?.() ?? '—'} MWh<br/>` +
            `${props.is_excluded ? 'Excluded' : 'Eligible'}`
          )
          .addTo(mapObj.current)
      })

      mapObj.current.on('mouseleave', 'scan-circles', () => {
        mapObj.current.getCanvas().style.cursor = ''
        popup.remove()
      })

      mapObj.current.on('click', 'scan-circles', (e) => {
        const props = e.features[0].properties
        const coords = e.features[0].geometry.coordinates
        onScanPointClick?.({ lat: coords[1], lng: coords[0], score: props.overall_score })
      })
    })

    return () => {
      mapObj.current?.remove()
      mapObj.current = null
    }
  }, [])

  // Click handler
  useEffect(() => {
    const map = mapObj.current
    if (!map) return

    const handler = (e) => {
      if (activeTab === 'assess' || activeTab === 'compare') {
        onMapClick?.({ lat: e.lngLat.lat, lng: e.lngLat.lng })
      }
    }
    map.on('click', handler)
    return () => map.off('click', handler)
  }, [activeTab, onMapClick])

  // Click-to-assess crosshair marker
  useEffect(() => {
    if (!mapObj.current) return

    if (markerEl.current) {
      markerEl.current.remove()
      markerEl.current = null
    }

    if (markerPos) {
      const el = document.createElement('div')
      el.style.cssText = [
        'width:20px', 'height:20px', 'border-radius:50%',
        'background:rgba(59,130,246,.9)',
        'border:2px solid #fff',
        'box-shadow:0 0 0 4px rgba(59,130,246,.3)',
        'cursor:crosshair',
      ].join(';')
      markerEl.current = new maplibregl.Marker({ element: el })
        .setLngLat([markerPos.lng, markerPos.lat])
        .addTo(mapObj.current)
    }
  }, [markerPos])

  // Assessment result score badge
  useEffect(() => {
    if (!mapObj.current || !assessmentMarker) return
    const { lat, lon, score } = assessmentMarker
    const color = scoreToColor(score)
    const el = document.createElement('div')
    el.style.cssText = [
      `width:36px`, `height:36px`, `border-radius:50%`,
      `background:${color}22`,
      `border:2px solid ${color}`,
      `display:flex`, `align-items:center`, `justify-content:center`,
      `font-size:12px`, `font-weight:700`, `color:${color}`,
      `cursor:pointer`,
    ].join(';')
    el.textContent = Math.round(score)
    new maplibregl.Marker({ element: el })
      .setLngLat([lon, lat])
      .addTo(mapObj.current)
  }, [assessmentMarker])

  // Compare queue markers
  useEffect(() => {
    if (!mapObj.current) return
    compareQueue?.forEach((site, i) => {
      const el = document.createElement('div')
      el.style.cssText = [
        'width:22px', 'height:22px', 'border-radius:50%',
        'background:rgba(34,211,238,.9)',
        'border:2px solid #fff',
        'display:flex', 'align-items:center', 'justify-content:center',
        'font-size:11px', 'font-weight:700', 'color:#000',
        'cursor:default',
      ].join(';')
      el.textContent = i + 1
      new maplibregl.Marker({ element: el })
        .setLngLat([site.lon, site.lat])
        .addTo(mapObj.current)
    })
  }, [compareQueue])

  // Scan results layer update
  useEffect(() => {
    const map = mapObj.current
    if (!map || !map.isStyleLoaded()) return
    const source = map.getSource('scan-source')
    if (source && scanResult) {
      source.setData(scanResult)
    }
  }, [scanResult])

  // flyTo helper exposed via ref
  const flyTo = useCallback(([lon, lat], zoom = 6) => {
    mapObj.current?.flyTo({ center: [lon, lat], zoom, duration: 1200 })
  }, [])

  // Expose flyTo via a data attribute hack so parent can call it
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current._flyTo = flyTo
    }
  }, [flyTo])

  return (
    <div
      ref={mapRef}
      className="app__map"
      style={{ width: '100%', height: '100%' }}
    />
  )
}
